import React, { useState } from 'react';
import { Copy, Type, Clock, Hash, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export function WordCounter() {
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);

  const wordCount = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
  const charCount = text.length;
  const charNoSpaces = text.replace(/\s/g, '').length;
  const sentenceCount = text.trim() === '' ? 0 : text.split(/[.!?]+/).filter(Boolean).length;
  const paragraphCount = text.trim() === '' ? 0 : text.split(/\n\s*\n/).filter(Boolean).length;
  const readingTime = Math.ceil(wordCount / 200); // Average reading speed of 200 words per minute

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>Word Counter & Text Analyzer | DigiVault.club</title>
        <meta name="description" content="Free online word counter and text analysis tool. Count words, characters, sentences, and paragraphs. Calculate reading time and get detailed text statistics." />
        <meta name="keywords" content="word counter, character counter, text analyzer, reading time calculator, text statistics" />
        <link rel="canonical" href="https://digivault.club/word-counter" />
      </Helmet>
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Type className="w-8 h-8 text-emerald-500" />
              <h1 className="text-3xl font-bold text-white">Word Counter</h1>
            </div>

            <div className="bg-gray-800 rounded-xl p-6 shadow-xl mb-8">
              <div className="mb-6">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type or paste your text here..."
                  className="w-full h-64 bg-gray-700 rounded-lg px-4 py-3 text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg px-4 py-2 text-white transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy Text'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-500 mb-2">
                    <Hash className="w-5 h-5" />
                    <span className="font-semibold">Words</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{wordCount}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-500 mb-2">
                    <Type className="w-5 h-5" />
                    <span className="font-semibold">Characters</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{charCount}</p>
                  <p className="text-sm text-gray-400">({charNoSpaces} without spaces)</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-500 mb-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-semibold">Reading Time</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{readingTime} min</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-500 mb-2">
                    <Type className="w-5 h-5" />
                    <span className="font-semibold">Sentences</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{sentenceCount}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-500 mb-2">
                    <Type className="w-5 h-5" />
                    <span className="font-semibold">Paragraphs</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{paragraphCount}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-6 shadow-xl">
              <h2 className="text-2xl font-bold mb-4 text-white">About Word Counter</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Our Word Counter tool helps you analyze your text in detail. Whether you're writing an essay,
                  article, or any other content, this tool provides valuable metrics about your text:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Accurate word count for any text</li>
                  <li>Character count (with and without spaces)</li>
                  <li>Sentence and paragraph counting</li>
                  <li>Estimated reading time based on average reading speed</li>
                  <li>Easy copy functionality for your convenience</li>
                </ul>
                <p>
                  Perfect for students, writers, content creators, and anyone who needs to keep track of their text metrics.
                  The tool works in real-time, updating counts as you type or paste text.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}